// RFC 5545 .ics calendar file generator (client-side, no server round trip)
// Used to let guests add a confirmed RSVP event to their calendar.

import type { Event } from '@/types/event';

/**
 * Escape text for ICS per RFC 5545 §3.3.11
 */
const escapeIcsText = (input: string | undefined | null): string => {
  if (!input) return '';
  return input
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
};

/**
 * Format a Date as ICS UTC timestamp: YYYYMMDDTHHMMSSZ
 */
const formatIcsUtc = (date: Date): string => {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
};

/**
 * Format a Date as ICS all-day date: YYYYMMDD
 */
const formatIcsDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
};

/**
 * Build a Date object from an event's rsvpDate (YYYY-MM-DD) and optional rsvpTime (HH:mm).
 * Returns null if the date is missing or invalid.
 */
const buildEventDate = (rsvpDate?: string, rsvpTime?: string): { start: Date; end: Date; allDay: boolean } | null => {
  if (!rsvpDate) return null;

  if (rsvpTime) {
    const start = new Date(`${rsvpDate}T${rsvpTime}`);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000); // default 2h
    return { start, end, allDay: false };
  }

  const start = new Date(`${rsvpDate}T00:00:00`);
  if (isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { start, end, allDay: true };
};

/**
 * Build RFC 5545 ICS content for an event.
 * Returns null if the event has no usable date.
 */
export const buildIcsContent = (event: Pick<Event, 'id' | 'name' | 'description' | 'hostName' | 'rsvpDate' | 'rsvpTime' | 'rsvpLocation'>): string | null => {
  const dt = buildEventDate(event.rsvpDate, event.rsvpTime);
  if (!dt) return null;

  const stamp = formatIcsUtc(new Date());
  const uid = `${event.id}@eventdatepicker.com`;

  const dtStart = dt.allDay
    ? `DTSTART;VALUE=DATE:${formatIcsDate(dt.start)}`
    : `DTSTART:${formatIcsUtc(dt.start)}`;
  const dtEnd = dt.allDay
    ? `DTEND;VALUE=DATE:${formatIcsDate(dt.end)}`
    : `DTEND:${formatIcsUtc(dt.end)}`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//EventDatePicker//RSVP//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    dtStart,
    dtEnd,
    `SUMMARY:${escapeIcsText(event.name)}`,
    event.description ? `DESCRIPTION:${escapeIcsText(event.description)}` : '',
    event.rsvpLocation ? `LOCATION:${escapeIcsText(event.rsvpLocation)}` : '',
    `ORGANIZER;CN=${escapeIcsText(event.hostName)}:mailto:noreply@eventdatepicker.com`,
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-PT30M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Event reminder',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  return lines.join('\r\n');
};

/**
 * Trigger a browser download of an .ics file for the event.
 * Returns true if the download was initiated, false if the event has no date to export.
 */
export const downloadIcsForEvent = (event: Pick<Event, 'id' | 'name' | 'description' | 'hostName' | 'rsvpDate' | 'rsvpTime' | 'rsvpLocation'>): boolean => {
  const content = buildIcsContent(event);
  if (!content) return false;

  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const safeName = (event.name || 'event').replace(/[^a-z0-9]+/gi, '_').toLowerCase().slice(0, 60);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName || 'event'}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
};
